import { useEffect, useState, useRef } from 'react';
import { useGcsStore } from '../store/useGcsStore';
import { MissionFailureMode } from '../types/gcs/mission';
import type { VehicleId } from '../types/gcs/vehicle';

const FAILURE_MODE_BLINK_DURATION_MS = 10000; // Total duration of blinking effect (10 seconds)
const FAILURE_MODE_BLINK_PERIOD_MS = 500; // Time between color alternations (0.5 seconds)

interface FailureModeBlinkState {
  isBlinking: boolean;
  showRedBackground: boolean;
}

export function useFailureModeBlinkEffect(gcsId: string, vehicleId: VehicleId): FailureModeBlinkState {
  const [isBlinking, setIsBlinking] = useState(false);
  const [showRedBackground, setShowRedBackground] = useState(false);
  const blinkTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const selectFailureMode = (): MissionFailureMode | undefined => {
      const gcsData = useGcsStore.getState().data.get(gcsId);
      if (!gcsData) return undefined;

      const liveMission = gcsData.liveMissionsMap.get(vehicleId);
      if (!liveMission) return undefined;

      const missionState = gcsData.missionDetailedStateMap.get(liveMission.missionId);
      return missionState?.failureMode;
    };

    const unsubscribe = useGcsStore.subscribe(selectFailureMode, (nextMode, prevMode) => {
      // Check if mode changed from nominal to non-nominal
      if (
        prevMode === MissionFailureMode.NOMINAL &&
        nextMode !== undefined &&
        nextMode !== MissionFailureMode.NOMINAL
      ) {
        // Clear any existing timeout
        if (blinkTimeoutRef.current) {
          clearTimeout(blinkTimeoutRef.current);
        }

        // Start blinking
        setIsBlinking(true);
        setShowRedBackground(false);

        // Stop blinking after duration
        blinkTimeoutRef.current = setTimeout(() => {
          setIsBlinking(false);
          setShowRedBackground(false);
          blinkTimeoutRef.current = null;
        }, FAILURE_MODE_BLINK_DURATION_MS);
      }
      // Check if mode changed to nominal (stop blinking immediately)
      else if (nextMode === MissionFailureMode.NOMINAL && prevMode !== MissionFailureMode.NOMINAL) {
        // Clear timeout and stop blinking
        if (blinkTimeoutRef.current) {
          clearTimeout(blinkTimeoutRef.current);
          blinkTimeoutRef.current = null;
        }
        setIsBlinking(false);
        setShowRedBackground(false);
      }
    });

    return () => {
      unsubscribe();
      // Clear timeout on unmount or dependency change
      if (blinkTimeoutRef.current) {
        clearTimeout(blinkTimeoutRef.current);
        blinkTimeoutRef.current = null;
      }
    };
  }, [gcsId, vehicleId]);

  useEffect(() => {
    if (!isBlinking) return;

    // Alternate background color at regular intervals
    const interval = setInterval(() => {
      setShowRedBackground((prev) => !prev);
    }, FAILURE_MODE_BLINK_PERIOD_MS);

    return () => clearInterval(interval);
  }, [isBlinking]);

  return { isBlinking, showRedBackground };
}
